import { apiRequest } from '@/domains/api';
import type {
  IAccountCompletion,
  IIdpInvitationRequest,
  IMailDelivery,
  IRegistrationCompleted,
  IRegistrationCompletion,
  IRegistrationView,
  ISession,
  IUser,
  IVerificationRequest,
  RegistrationCaller,
  RegistrationStart,
  TeamRoutingAnswers
} from './types';

/**
 * Auth transport — survey-api's `/auth` surface.
 *
 * The way in is the exchange, `POST /auth/sessions`: the access token Invent's IdP issued this
 * browser (`idp.ts`), traded once for survey-api's own session. Notably absent is a refresh:
 * sessions are server-side rows behind an opaque token, so a session either lives until
 * `expiresAt` or is gone.
 *
 * Beside the group sits the registration surface, `/auth/registrations` (SR-213), which
 * replaced `POST /auth/register`: the run's start, its read and its nine transitions, all
 * read here — two of them, `no-anchor` and `routing`, TEAMS' alone, and one, `redirection`,
 * RIA_FIRM's (SR-416).
 *
 * Somebody whose account signs in nowhere yet asks for an invitation to the IdP,
 * `POST /auth/idp-invitations/requests` — anonymous, since asking is what somebody who cannot
 * sign in does. The invitation itself is the IdP's, redeemed there; nothing here reads it.
 *
 * Two routes confirm an address (survey-api's self-service plan, R13–R14): the anonymous
 * redemption of a confirmation link, `POST /auth/email/verification`, and the owner asking for
 * another one, `POST /auth/email/verification-requests`. Neither is a way in — a confirmation
 * issues no session — and what they change gates nothing: `/auth/me`
 * reports it as `emailVerified`, and the app prompts from that.
 *
 * The owner does not move the address: staff do, with a reason (SR-487, survey-api's PFR §7), and
 * the two self-service routes are gone.
 *
 * A point of contact a TEAMS or RIA_FIRM response named accepts its invitation from the mail,
 * `POST /auth/invitations/acceptance` — anonymous, and the one way an account comes to act for a
 * participant as its `contact` (SR-416, survey-api's B5-16).
 *
 * `GET /auth/mail-delivery` is the one route that is about the deployment rather than an
 * account: it says whether this server holds its mail to an allowlist, which is what every
 * form causing a mail reads before it decides whether to ask for a test inbox. See
 * `mail-delivery.ts`.
 */

/** `SessionResponse` — the wire shape, before it becomes an `ISession`. */
interface ISessionResponse {
  token: string;
  expiresAt: string;
  kind: ISession['kind'];
}

export const authApi = {
  /**
   * Trades the IdP's access token for survey-api's session, once — anonymous, since no session
   * exists yet. **An empty 401** for a token survey-api does not accept and for a disabled
   * account alike, one answer by design; **403 `account-not-linked`** for a valid token whose
   * identity no account holds and no live invitation links. Both reach the caller as the
   * `ApiError` they arrived as: which screen each becomes is `/auth/callback`'s.
   */
  async exchange(accessToken: string): Promise<ISession> {
    const { token, expiresAt, kind } = await apiRequest<ISessionResponse>('/auth/sessions', {
      method: 'POST',
      anonymous: true,
      body: { accessToken }
    });
    return { token, expiresAt, kind };
  },

  me(signal?: AbortSignal): Promise<IUser> {
    return apiRequest<IUser>('/auth/me', { signal });
  },

  async logout(signal?: AbortSignal): Promise<void> {
    await apiRequest<void>('/auth/logout', { method: 'POST', signal }).catch((error) => {
      // A failed server-side logout must not strand the user in the app —
      // the local session is dropped either way.
      console.warn('[Auth] Logout request failed:', error);
    });
  },

  /**
   * Asks for an invitation to sign in at the IdP, for the account at `email`.
   *
   * **202 for every address**, known or not, and for a string that is not one plain address at
   * all — anything else would turn the form into a test for who has an account here. survey-api's
   * worker resolves the account: an unknown, disabled or already linked one gets nothing, and a
   * live invitation at the same address is sent again. Past survey-api's per-account limit a
   * request sends nothing.
   *
   * The one refusal a caller can meet is about `deliverTo`, a 400 on that field, judged on the
   * request's own strings before any lookup for the same reason — see `useTestInbox`.
   */
  async requestIdpInvitation(request: IIdpInvitationRequest): Promise<void> {
    await apiRequest<void>('/auth/idp-invitations/requests', {
      method: 'POST',
      anonymous: true,
      body: request
    });
  },

  /**
   * Redeems an address confirmation — the link survey-api mails at registration and on request
   * (`/email/verify#token=…`). Anonymous, and it has to be: the link is opened wherever the mail
   * is read, signed in or not.
   *
   * **204** on a claim, which stamps `email_verified_at` if it was not stamped already — a
   * token redeemed after the address was confirmed another way still answers 204 and is spent.
   * **An empty 400** for every other token: missing, invented, expired, spent, or one minted for
   * another purpose. One answer by design, so the page says one thing for all of them. It issues
   * no session and ends none.
   */
  async verifyEmail(token: string): Promise<void> {
    await apiRequest<void>('/auth/email/verification', {
      method: 'POST',
      anonymous: true,
      body: { token }
    });
  },

  /**
   * Asks for another confirmation link, mailed to the caller's own address.
   *
   * **202 whether or not a mail was queued**: an address already confirmed needs none, and the
   * caller reads which from `/auth/me`. Its only effect is the mail, so `deliverTo` is judged by
   * the whole table against the caller's address, `missing` included — the refusal
   * `useTestInbox` words. Past survey-api's limit — the reset limit's numbers, counted apart — a
   * request mints and sends nothing, and the last link stays good.
   */
  async requestEmailVerification(request: IVerificationRequest): Promise<void> {
    await apiRequest<void>('/auth/email/verification-requests', {
      method: 'POST',
      body: request
    });
  },

  /**
   * Accepts a point-of-contact invitation — the link survey-api's worker mails to the address a
   * TEAMS or RIA_FIRM response named (`/invitations/accept#token=…`). Anonymous: the mail is read
   * wherever it is read. **No route reads an invitation by its token**, so nothing here can say
   * whose it is or where it went; the mail does.
   *
   * **204, empty, no session**: the account with the invited address — made here, an advisor,
   * where none exists — now acts for the participant as its point of contact, and its address is
   * confirmed. An account that holds no IdP identity also gets an invitation to sign in at that
   * address. **An empty 400** for every token that cannot be used (unknown, spent, revoked,
   * replaced, past its fourteen days).
   */
  async acceptInvitation(token: string): Promise<void> {
    await apiRequest<void>('/auth/invitations/acceptance', {
      method: 'POST',
      anonymous: true,
      body: { token }
    });
  },

  /**
   * How this deployment delivers mail. Anonymous, because the invitation request screen is, and
   * it reads configuration and no account, so it answers every caller alike.
   */
  mailDelivery(signal?: AbortSignal): Promise<IMailDelivery> {
    return apiRequest<IMailDelivery>('/auth/mail-delivery', { anonymous: true, signal });
  },
};

/**
 * The registration run (SR-213), in the caller's mode (`RegistrationCaller`).
 *
 * **Anonymous** on `/registration` — a registrant has no account until the last call creates
 * one — so no bearer goes out, and a session this browser happens to hold is neither sent nor put
 * at risk by a refusal there. **Account** on `/portal/surveys/add`: every call carries the session's
 * bearer, which is what binds the run to that account in survey-api (its teams plan R19) — and a
 * bound run is an empty 404 to anybody else, anonymous callers included — and the 401 handler
 * applies as it does to any signed-in call.
 *
 * Every refusal reaches the caller as the `ApiError` it arrived as. Which screen a refusal
 * becomes is the page's decision, and a copy of it in each of these nine would be nine places
 * for the two to drift apart.
 */
export function registrationApi(caller: RegistrationCaller) {
  const anonymous = caller === 'anonymous';

  return {
    /**
     * Opens a run — 201 with the first view: a person's by the numbers they hold, or, signed in,
     * a firm's or a team's (`RegistrationStart`). No cycle is asked about: registration opens no
     * response (survey-api's teams plan R22). A 404 means there is no such registration here — a
     * survey started anonymously, or FA and FSP, which are numbers since SR-487. 400 on
     * `numbers`; 409 `several-persons` for a signed-in numbers run of an account that is several
     * people, whose number staff add by hand.
     */
    startRegistration(start: RegistrationStart): Promise<IRegistrationView> {
      return apiRequest<IRegistrationView>('/auth/registrations', {
        method: 'POST',
        anonymous,
        body: start
      });
    },

    /** What a resumed tab reads. A run that no longer takes steps answers 409
     *  `registration-out-of-step` with where it stopped, not a view. */
    readRegistration(runId: string, signal?: AbortSignal): Promise<IRegistrationView> {
      return apiRequest<IRegistrationView>(registrationPath(runId), { anonymous, signal });
    },

    /**
     * `idType` is sent only when the registrant picked one; left out, the server reads the number
     * as the view's first `idTypes`. `issuingState` is a licence's state, a USPS code
     * (`US_STATES`): required on `INSURANCE_LICENSE`, 400 on any other type. A CRD's first
     * `not_found` stays at `anchor` with `lookup: 'not_found'`; a second, or `error`, moves on
     * with no message. An insurance number is looked up in no registry (PFR D8).
     */
    submitAnchor(
      runId: string,
      value: string,
      idType?: string,
      issuingState?: string
    ): Promise<IRegistrationView> {
      return apiRequest<IRegistrationView>(registrationPath(runId, 'anchor'), {
        method: 'POST',
        anonymous,
        body: { value, idType, issuingState }
      });
    },

    /** TEAMS at the anchor: the lead advisor has no CRD. The run goes to `route` with no number
     *  and no lookup, at the question a team with no firm is asked first. Any other survey
     *  answers 400 on `value` — the page offers it on TEAMS alone. No body. */
    declareNoAnchor(runId: string): Promise<IRegistrationView> {
      return apiRequest<IRegistrationView>(registrationPath(runId, 'no-anchor'), {
        method: 'POST',
        anonymous
      });
    },

    /**
     * The route step's answers — **every one so far, each time**, since survey-api keeps none
     * between calls. 200 with the run still on `route` and the next question, on `complete`, or
     * on `redirected` (abandoned, nothing created). A 400 names the answer it cannot take under
     * `errors['answers.<code>']`.
     */
    submitRouting(runId: string, answers: TeamRoutingAnswers): Promise<IRegistrationView> {
      return apiRequest<IRegistrationView>(registrationPath(runId, 'routing'), {
        method: 'POST',
        anonymous,
        body: { answers }
      });
    },

    /**
     * The answer to "is this you?". `false` is not an error: the run returns to the same number's
     * anchor. survey-api takes a third answer, `disputed` (PFR D11), which this app never sends:
     * page 03 keeps the wireframe's two answers (Q5).
     *
     * `sameTeam` is a new TEAMS team's answer to "is this your team?" (SR-416's B5-11): one of
     * the view's `sightings`, sent only when given — absent is a new team, which goes on to the
     * route step. survey-api refuses an id it did not show 400 at `sameTeam`; a team an account
     * already acts for ends the run 409 `identifier_unavailable`, a team none does takes the run
     * to `complete` under the team's name, and the completion links the new account to it.
     */
    decideIdentity(
      runId: string,
      confirmed: boolean,
      sameTeam?: string
    ): Promise<IRegistrationView> {
      return apiRequest<IRegistrationView>(registrationPath(runId, 'identity'), {
        method: 'POST',
        anonymous,
        body: sameTeam === undefined ? { confirmed } : { confirmed, sameTeam }
      });
    },

    /**
     * RIA_FIRM's way out of its anchor (SR-416's B5-12): the SEC does not list the firm's
     * number, and the registrant takes the team survey instead. 200 with the view on
     * `redirected`, `destinationSurvey: 'TEAMS'` — the run abandoned in the same request, nothing
     * made. Any other run — a number that matched or could not be checked, another survey, a
     * second press — is 409 out-of-step. No body.
     */
    redirectRegistration(runId: string): Promise<IRegistrationView> {
      return apiRequest<IRegistrationView>(registrationPath(runId, 'redirection'), {
        method: 'POST',
        anonymous
      });
    },

    /** Back to the anchor from a step that no longer asks for the number. No body. */
    reviseRegistration(runId: string): Promise<IRegistrationView> {
      return apiRequest<IRegistrationView>(registrationPath(runId, 'revision'), {
        method: 'POST',
        anonymous
      });
    },

    /** Ends the run — 204 and nothing to render, since no step is left. No body. */
    async abandonRegistration(runId: string): Promise<void> {
      await apiRequest<void>(registrationPath(runId, 'abandonment'), {
        method: 'POST',
        anonymous
      });
    },

    /**
     * Anonymous: creates the account and the person — no response, which the survey list starts
     * (PFR D3) — and queues the address confirmation mail and the registrant's invitation to sign
     * in at the IdP, whose expiry the answer carries (`invitationExpiresAt`). **Issues no
     * session**: signing in is the IdP's, through that invitation. A `deliverTo` that is named is
     * judged, and one that is not is never refused — the mail is withheld instead
     * (`IRegistrationCompletion`). A person run's `fields` are `legalFirstName` and
     * `legalLastName`.
     *
     * Account: `{fields}` alone (`IAccountCompletion`). Nothing is created but what the run needs
     * — a team or firm linked to the caller, or a number added to the caller's own person (one
     * made where the account holds none) — and no mail. 409 `several-persons` and
     * `identifier-already-yours` as at the start and the anchor.
     */
    completeRegistration(
      runId: string,
      completion: IRegistrationCompletion | IAccountCompletion
    ): Promise<IRegistrationCompleted> {
      return apiRequest<IRegistrationCompleted>(registrationPath(runId, 'completion'), {
        method: 'POST',
        anonymous,
        body: completion
      });
    }
  };
}

function registrationPath(runId: string, transition?: string): string {
  const run = `/auth/registrations/${encodeURIComponent(runId)}`;
  return transition ? `${run}/${transition}` : run;
}
